<template>
  <div>
    <jor_18bar />
    <v-container fluid>
      <v-row dense class="mb-2">
        <v-col cols="6" md="3">
          <v-card outlined class="pa-3 text-center dash-card">
            <v-icon color="primary" size="26">mdi-format-list-numbered</v-icon>
            <div class="text-h5 font-weight-bold mt-1">{{ statOrDash(stats.total_positions) }}</div>
            <div class="caption grey--text">ตำแหน่งทั้งหมด</div>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card outlined class="pa-3 text-center dash-card">
            <v-icon color="success" size="26">mdi-account-check</v-icon>
            <div class="text-h5 font-weight-bold mt-1">
              {{ statOrDash(stats.occupied) }} <span class="grey--text text-body-2">/ {{ statOrDash(stats.vacant) }} ว่าง</span>
            </div>
            <div class="caption grey--text">มีผู้ครอง / ว่าง</div>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card outlined class="pa-3 text-center dash-card">
            <v-icon color="warning" size="26">mdi-clock-alert-outline</v-icon>
            <div class="text-h5 font-weight-bold mt-1">{{ statOrDash(stats.retiring_this_year) }}</div>
            <div class="caption grey--text">ใกล้เกษียณปีนี้</div>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card outlined class="pa-3 text-center dash-card">
            <v-icon color="info" size="26">mdi-transit-transfer</v-icon>
            <div class="text-h5 font-weight-bold mt-1">
              {{ statOrDash(stats.reallocation_pending) }}
              <v-chip
                v-if="stats.reallocation_ready_to_confirm > 0"
                x-small
                color="error"
                text-color="white"
                class="ml-1"
              >
                {{ stats.reallocation_ready_to_confirm }} พร้อมยืนยัน
              </v-chip>
            </div>
            <div class="caption grey--text">รอเกลี่ยอัตรากำลัง</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- สถิติจำนวนตำแหน่งแต่ละประเภท ทั้งครองและว่าง -->
      <v-card outlined class="mb-3">
        <v-card-title class="py-2 subtitle-2">
          <v-icon left small color="primary">mdi-table-large</v-icon>
          สถิติตำแหน่งแยกตามประเภท
        </v-card-title>
        <v-data-table
          :headers="positionTypeHeaders"
          :items="stats.by_position_type || []"
          item-key="positions"
          dense
          class="position-type-table"
          :items-per-page="5"
          :footer-props="{ itemsPerPageOptions: [5, 10, 25, -1] }"
        >
          <template v-slot:item.total="{ item }">{{ formatNum(item.total) }}</template>
          <template v-slot:item.occupied="{ item }">
            <span class="success--text">{{ formatNum(item.occupied) }}</span>
          </template>
          <template v-slot:item.vacant="{ item }">
            <span class="warning--text">{{ formatNum(item.vacant) }}</span>
          </template>
          <template v-slot:item.ratio="{ item }">
            <div class="d-flex" style="height:8px;min-width:140px;border-radius:4px;overflow:hidden;background:#eee;">
              <div class="success" :style="`width:${occupiedPct(item)}%`"></div>
              <div class="warning" :style="`width:${100 - occupiedPct(item)}%`"></div>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <!-- แจ้งเตือนความผิดปกติของข้อมูล: ผู้ครองซ้อน / ข้อมูลไม่ตรง / เกษียณเกินกำหนด -->
      <v-card outlined class="mb-3" :class="anomalyTotal > 0 ? 'anomaly-card--warn' : ''">
        <v-toolbar flat dense :color="anomalyTotal > 0 ? 'error lighten-5' : 'success lighten-5'">
          <v-icon :color="anomalyTotal > 0 ? 'error darken-2' : 'success darken-2'" class="mr-2">
            {{ anomalyTotal > 0 ? 'mdi-alert-circle' : 'mdi-check-circle' }}
          </v-icon>
          <v-toolbar-title class="text-subtitle-2 font-weight-bold">
            {{ anomalyTotal > 0 ? `ตรวจพบความผิดปกติ ${anomalyTotal} รายการ` : 'ไม่พบความผิดปกติของข้อมูล' }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn small text @click="loadAnomalies" :loading="anomaliesLoading">
            <v-icon left small>mdi-refresh</v-icon>ตรวจสอบใหม่
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-3" v-if="anomalyTotal > 0">
          <v-row dense>
            <v-col cols="12" sm="6" md="3" v-for="cat in anomalyCategories" :key="cat.key">
              <v-alert
                v-if="anomalies.counts && anomalies.counts[cat.key] > 0"
                :type="cat.type"
                dense
                text
                class="mb-0"
              >
                <div class="d-flex justify-space-between align-center">
                  <span class="caption font-weight-bold">{{ cat.label }}</span>
                  <v-btn x-small text :color="cat.color" @click="anomalyDialogTab = cat.key; anomalyDialog = true">
                    {{ anomalies.counts[cat.key] }} รายการ
                  </v-btn>
                </div>
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- แผนที่ความหนาแน่นกรอบอัตรากำลัง -->
      <div class="mb-3">
        <PositionDensityMap
          :province-stats="provinceStats"
          :criterion-rows="criterionRows"
          :criterion-year="stats.latest_rate_work_g_year"
        />
      </div>

      <!-- Dialog: รายละเอียดความผิดปกติ -->
      <v-dialog v-model="anomalyDialog" max-width="900px">
        <v-card>
          <v-card-title class="error darken-2 white--text">
            <v-icon left color="white">mdi-alert-circle</v-icon>
            รายละเอียดความผิดปกติ
          </v-card-title>
          <v-tabs v-model="anomalyDialogTab" show-arrows>
            <v-tab v-for="cat in anomalyCategories" :key="cat.key" :href="`#${cat.key}`">
              {{ cat.label }} ({{ (anomalies.counts && anomalies.counts[cat.key]) || 0 }})
            </v-tab>
          </v-tabs>
          <v-card-text class="pt-4" style="max-height:70vh;overflow-y:auto;">
            <v-tabs-items v-model="anomalyDialogTab">
              <v-tab-item value="duplicate_incumbent">
                <v-simple-table dense>
                  <thead>
                    <tr><th>เลขบัตร</th><th>ชื่อ</th><th>จำนวนตำแหน่งที่ครอง</th><th>รายการตำแหน่ง</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in anomalies.duplicate_incumbent" :key="r.id_card">
                      <td>{{ r.id_card }}</td>
                      <td>{{ r.full_name }}</td>
                      <td>{{ r.position_count }}</td>
                      <td class="caption">{{ r.positions_held }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-tab-item>
              <v-tab-item value="stale_incumbent">
                <v-simple-table dense>
                  <thead>
                    <tr><th>เลขที่ตำแหน่ง</th><th>วิทยาลัย</th><th>ชื่อใน จอร.18</th><th>ชื่อปัจจุบัน</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="(r, i) in anomalies.stale_incumbent" :key="i">
                      <td>{{ r.id_position }} ({{ r.positions }})</td>
                      <td>{{ r.j18college_code }}</td>
                      <td>{{ r.jor18_name }}</td>
                      <td>{{ r.current_name }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-tab-item>
              <v-tab-item value="incumbent_not_found">
                <v-simple-table dense>
                  <thead>
                    <tr><th>เลขที่ตำแหน่ง</th><th>วิทยาลัย</th><th>เลขบัตร</th><th>ชื่อใน จอร.18</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="(r, i) in anomalies.incumbent_not_found" :key="i">
                      <td>{{ r.id_position }} ({{ r.positions }})</td>
                      <td>{{ r.j18college_code }}</td>
                      <td>{{ r.id_card }}</td>
                      <td>{{ r.jor18_name }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-tab-item>
              <v-tab-item value="overdue_retiree">
                <v-simple-table dense>
                  <thead>
                    <tr><th>เลขที่ตำแหน่ง</th><th>วิทยาลัย</th><th>ชื่อ</th><th>ปีที่ควรเกษียณ</th><th>ปีงบประมาณปัจจุบัน</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="(r, i) in anomalies.overdue_retiree" :key="i">
                      <td>{{ r.id_position }} ({{ r.positions }})</td>
                      <td>{{ r.j18college_code }}</td>
                      <td>{{ r.jor18_name }}</td>
                      <td>{{ r.expected_retire_year }}</td>
                      <td>{{ r.current_fiscal_year }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="anomalyDialog = false">ปิด</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <base-material-card
        icon="mdi-clipboard-text"
        title="ข้อมูลประเภทสถานศึกษา"
        class="px-5 py-3"
      >
        <v-card class="mb-4 pa-2">
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="ค้นหา ระบุคำ หรือ ส่วนข้อความเกี่ยวข้อง"
                single-line
                hide-details
                dense
                filled
                clearable
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-btn
                block
                large
                color="primary"
                @click="jor18Add"
              >
                <v-icon left>mdi-plus-circle-outline</v-icon>
                เพิ่มรายการ
              </v-btn>
            </v-col>

            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="collegesSelect"
                :items="colleges"
                item-text="college_name"
                item-value="college_code"
                outlined
                dense
                label="ค้นหาวิทยาลัย"
                clearable
                @change="jor18sSearch"
              />

              <v-btn
                color="info"
                outlined
                block
                class="mb-2"
                @click="searchDirector"
              >
                <v-icon left>mdi-magnify</v-icon>
                ค้นหาตำแหน่งว่าง ผอ.
              </v-btn>

              <v-btn
                color="info"
                outlined
                block
                class="mb-2"
                @click="searchSecDirector"
              >
                <v-icon left>mdi-magnify</v-icon>
                ค้นหาตำแหน่งว่าง รอง ผอ.
              </v-btn>

              <v-btn
                color="info"
                outlined
                block
                @click="searchTeacher"
              >
                <v-icon left>mdi-magnify</v-icon>
                ค้นหาตำแหน่งว่าง ครู
              </v-btn>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="idPositionSelect"
                outlined
                dense
                label="ค้นหาเลขที่ตำแหน่ง"
                clearable
                @keypress.enter="jor18sIDpostionSearch"
              />

              <v-btn
                block
                color="info"
                class="mb-2"
                :loading="loading"
                @click="exportVacantAll"
              >
                <v-icon left>mdi-file-excel</v-icon>
                ส่งออกตำแหน่งว่าง ทั้งหมด
              </v-btn>

              <v-btn
                block
                color="info"
                class="mb-2"
                :loading="loading"
                @click="exportTeacherSurvey"
              >
                <v-icon left>mdi-file-excel</v-icon>
                ส่งออกตำแหน่งว่าง ครู(สำรวจ)
              </v-btn>

              <v-btn
                block
                color="info"
                :loading="loading"
                @click="exportTeacherMove"
              >
                <v-icon left>mdi-file-excel</v-icon>
                ส่งออกตำแหน่งว่าง ครู(สำรวจ ย้าย)
              </v-btn>
            </v-col>

            <v-col cols="12" md="4">
              <v-btn
                block
                color="primary"
                class="mb-2"
                @click="man_powerCSV"
              >
                <v-icon left>mdi-file-import</v-icon>
                นำเข้าข้อมูลอัตราสำรวจ
              </v-btn>

              <v-btn
                block
                color="warning"
                class="mb-2"
                @click="updatej18background"
              >
                <v-icon left>mdi-pencil</v-icon>
                ปรับปรุงอัตรากำลังรับย้ายครู
              </v-btn>

              <v-btn
                block
                color="primary"
                class="mb-2"
                @click="SearchNull"
              >
                <v-icon left>mdi-search-web</v-icon>
                ตำแหน่งว่าง
              </v-btn>

              <v-btn
                block
                color="info"
                class="mb-2"
                @click="clearSearch"
              >
                <v-icon left>mdi-broom</v-icon>
                ล้างการค้นหา
              </v-btn>

              <v-btn
                block
                color="error"
                @click="clearSurvey"
              >
                <v-icon left>mdi-delete-sweep</v-icon>
                ลบรายการสำรวจ
              </v-btn>

              <v-btn
                block
                color="success"
                class="mt-2"
                @click="exportToExcel"
              >
                <v-icon left>mdi-file-excel</v-icon>
                Export Excel
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <!-- สรุปภาพรวมรายวิทยาลัย — แสดงเมื่อค้นหาด้วยวิทยาลัยเจาะจง -->
        <v-card
          v-if="collegeSummary"
          outlined
          class="mb-4 pa-3"
          :loading="collegeSummaryLoading"
        >
          <div class="d-flex align-center mb-2">
            <v-icon color="primary" class="mr-2">mdi-domain</v-icon>
            <span class="subtitle-1 font-weight-bold">{{ collegeSummary.college_name }}</span>
            <v-spacer />
            <span class="caption grey--text">
              ทั้งหมด {{ formatNum(collegeSummary.total) }} · ครอง {{ formatNum(collegeSummary.occupied) }} ·
              ว่าง {{ formatNum(collegeSummary.vacant) }}
            </span>
          </div>

          <v-row dense>
            <v-col cols="12" md="7">
              <v-simple-table dense>
                <thead>
                  <tr><th>ประเภทตำแหน่ง</th><th class="text-right">ทั้งหมด</th><th class="text-right">ครองแล้ว</th><th class="text-right">ว่าง</th></tr>
                </thead>
                <tbody>
                  <tr v-for="row in collegeSummary.by_type" :key="row.positions">
                    <td>{{ row.positions }}</td>
                    <td class="text-right">{{ formatNum(row.total) }}</td>
                    <td class="text-right success--text">{{ formatNum(row.occupied) }}</td>
                    <td class="text-right" :class="+row.vacant > 0 ? 'warning--text font-weight-bold' : ''">
                      {{ formatNum(row.vacant) }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
            <v-col cols="12" md="5">
              <div class="caption font-weight-bold mb-1">
                <v-icon small color="warning darken-3">mdi-clock-alert-outline</v-icon>
                จะเกษียณปีนี้ ({{ collegeSummary.retiring_this_year.length }} คน)
              </div>
              <div v-if="collegeSummary.retiring_this_year.length === 0" class="caption grey--text">
                ไม่มีผู้ที่จะเกษียณในปีงบประมาณนี้
              </div>
              <v-chip
                v-for="r in collegeSummary.retiring_this_year"
                :key="r.id_position"
                small
                outlined
                color="warning darken-3"
                class="mr-1 mb-1"
              >
                {{ r.full_name }} — {{ r.positions }} ({{ r.id_position }})
              </v-chip>
            </v-col>
          </v-row>
        </v-card>

        <v-data-table
          :headers="showHeaders"
          :items="displayedJor18s"
          :search="search"
          :loading="loading"
          class="elevation-1"
        >
          <template v-slot:top>
            <div class="d-flex flex-wrap align-center ma-2" style="gap: 12px;">
              <v-btn-toggle v-model="statusFilter" mandatory dense color="primary">
                <v-btn small value="all">ทั้งหมด</v-btn>
                <v-btn small value="occupied">มีคนครอง</v-btn>
                <v-btn small value="vacant">ว่าง</v-btn>
              </v-btn-toggle>
              <v-spacer />
              <span class="caption grey--text">
                แสดง {{ formatNum(displayedJor18s.length) }} จาก {{ formatNum(jor18s.length) }} รายการ
              </span>
            </div>
            <v-select
              v-model="selectedHeaders"
              :items="headers"
              label="เลือกคอลัมน์ที่ต้องการแสดง"
              multiple
              outlined
              dense
              return-object
              class="ma-2"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip v-if="index < 6" small>
                  <span>{{ item.text }}</span>
                </v-chip>
                <span v-if="index === 6" class="grey--text caption">
                  (+{{ selectedHeaders.length - 6 }} คอลัมน์)
                </span>
              </template>
            </v-select>
          </template>

          <template v-slot:item.titles="{ item }">
            {{ item.titles }}{{ item.first_name }} {{ item.last_name }}
          </template>

          <template v-slot:item.actionSelect="{ item }">
            <v-chip v-if="hasIdCard(item)" outlined color="success">
              มีคนครอง
            </v-chip>
            <div v-else>
              <div v-if="item.id_no === editedItem.id_no">
                <v-btn icon color="error" @click="close">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
                <v-btn icon color="success" @click="save">
                  <v-icon>mdi-content-save</v-icon>
                </v-btn>
              </div>
              <div v-else>
                <v-btn icon color="primary" @click="editItem(item)">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon color="error" @click="deleteItem(item.id_no)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
          </template>

          <template v-slot:item.j18_reason="{ item }">
            <v-autocomplete
              v-if="item.id_no === editedItem.id_no"
              v-model="editedItem.j18_reason"
              :items="reasons"
              item-text="text"
              item-value="value"
              outlined
              dense
              label="เหตุผล"
            />
            <span v-else>{{ item.j18_reason }}</span>
          </template>

          <template v-slot:item.j18_collegereport="{ item }">
            <v-radio-group
              v-if="item.id_no === editedItem.id_no"
              v-model="editedItem.j18_collegereport"
              row
            >
              <v-radio value="1" label="สำรวจ" color="success" />
              <v-radio value="0" label="ไม่สำรวจ" color="warning" />
            </v-radio-group>
            <div v-else>
              <v-icon v-if="item.j18_collegereport === 1" color="success">
                mdi-check-circle
              </v-icon>
              <v-icon v-else color="grey">
                mdi-close-circle-outline
              </v-icon>
            </div>
          </template>

          <template v-slot:item.j18_reason_etc="{ item }">
            <v-text-field
              v-if="item.id_no === editedItem.id_no"
              v-model="editedItem.j18_reason_etc"
              outlined
              dense
              label="เหตุผลอื่น"
            />
            <span v-else>{{ item.j18_reason_etc }}</span>
          </template>

          <template v-slot:item.j18_background="{ item }">
            <v-text-field
              v-if="item.id_no === editedItem.id_no"
              v-model="editedItem.j18_background"
              outlined
              dense
              label="เหตุการณ์"
            />
            <span v-else>{{ item.j18_background }}</span>
          </template>

          <template v-slot:item.status_booking="{ item }">
            <v-chip 
              :color="item.status_booking ? 'warning' : 'success'" 
              dark 
              small
            >
              {{ item.status_booking || 'ว่าง' }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              icon
              color="warning"
              @click="jor18Edit(item.id_position, item.positions)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </template>

          <template v-slot:item.action_s="{ item }">
            <v-btn
              icon
              color="error"
              @click="jor18Delete(item.id_position, item.positions)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>

          <template v-slot:no-results>
            <v-alert type="error" :value="true">
              ไม่พบผลลัพธ์ "{{ search }}" ที่คุณกำลังค้นหา
            </v-alert>
          </template>
        </v-data-table>
      </base-material-card>

      <!-- แผนเกลี่ยอัตรากำลังระหว่างสถานศึกษา -->
      <base-material-card
        icon="mdi-transit-transfer"
        title="แผนเกลี่ยอัตรากำลังระหว่างสถานศึกษา"
        class="px-5 py-3"
        color="info"
      >
        <v-alert type="info" outlined dense class="mb-3">
          รายการนี้คือ "แผน" ที่จะเกลี่ยอัตราไปสถานศึกษาปลายทาง — อัตรายังถือเป็นของสถานศึกษาต้นทางเดิม
          จนกว่าผู้ครองตำแหน่งจะพ้นตำแหน่งจริง (เกษียณ/ย้าย) แล้วกดยืนยันด้านล่างเท่านั้น ระบบจะไม่เปลี่ยน
          สังกัดใน จอร.18 ให้อัตโนมัติ — เมื่อกดยืนยันแล้วและเป็นตำแหน่ง "ครู" ระบบจะเพิ่มอัตรานี้เข้า
          อัตรารับย้าย (admin/man_power) ที่สถานศึกษาปลายทางให้ทันที พร้อมเข้าสู่กระบวนการรับย้ายปกติ
          (admin/process_transfer) โดยไม่ต้องเพิ่มเอง
        </v-alert>

        <v-row class="mb-2">
          <v-col cols="12" md="4">
            <v-btn block color="primary" @click="reallocationCSVdialog = true">
              <v-icon left>mdi-file-import</v-icon>
              นำเข้าไฟล์แผนเกลี่ยอัตรากำลัง (CSV)
            </v-btn>
          </v-col>
          <v-col cols="12" md="4">
            <v-btn-toggle v-model="reallocationStatusFilter" mandatory dense color="info">
              <v-btn value="pending" @click="reallocationQuery">รอดำเนินการ</v-btn>
              <v-btn value="completed" @click="reallocationQuery">ยืนยันแล้ว</v-btn>
              <v-btn value="cancelled" @click="reallocationQuery">ยกเลิกแล้ว</v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <v-data-table
          :headers="reallocationHeaders"
          :items="reallocations"
          :loading="reallocationLoading"
          class="elevation-1"
        >
          <template v-slot:item.position="{ item }">
            <div><strong>{{ item.id_position }}</strong> ({{ item.positions }})</div>
          </template>

          <template v-slot:item.route="{ item }">
            <div class="caption">
              {{ item.college_name_from_live || item.college_code_from }}
              <v-icon small>mdi-arrow-right</v-icon>
              {{ item.college_name_to_live || item.college_code_to }}
            </div>
          </template>

          <template v-slot:item.current_owner="{ item }">
            <div v-if="item.current_id_card">
              <div>{{ item.current_titles }}{{ item.current_first_name }} {{ item.current_last_name }}</div>
              <v-chip
                x-small
                :color="retireStatusColor(item.retire_verify_status)"
                text-color="white"
              >
                {{ retireStatusLabel(item.retire_verify_status) }}
                <template v-if="item.expected_retire_year"> ({{ item.expected_retire_year }})</template>
              </v-chip>
            </div>
            <v-chip v-else x-small color="success" text-color="white">ว่างแล้ว พร้อมยืนยัน</v-chip>
          </template>

          <template v-slot:item.note="{ item }">
            <span class="caption">{{ item.note }}</span>
          </template>

          <template v-slot:item.imported="{ item }">
            <div class="caption grey--text">
              {{ item.imported_by }}<br>{{ item.imported_at }}
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <div v-if="item.status === 'pending'">
              <v-btn small color="success" class="mr-1 mb-1" @click="confirmReallocation(item)">
                <v-icon left small>mdi-check</v-icon>ยืนยันย้าย
              </v-btn>
              <v-btn small color="error" outlined @click="cancelReallocation(item)">
                <v-icon left small>mdi-close</v-icon>ยกเลิก
              </v-btn>
            </div>
            <div v-else-if="item.status === 'completed'" class="caption">
              ยืนยันโดย {{ item.completed_by }}<br>{{ item.completed_at }}
            </div>
            <div v-else class="caption">
              ยกเลิกโดย {{ item.completed_by }}<br>{{ item.cancel_reason }}
            </div>
          </template>
        </v-data-table>
      </base-material-card>

      <!-- CSV Import Dialog: แผนเกลี่ยอัตรากำลัง -->
      <v-dialog v-model="reallocationCSVdialog" persistent max-width="600px">
        <v-card>
          <v-card-title class="info white--text">
            <v-icon left color="white">mdi-file-import</v-icon>
            นำเข้าไฟล์แผนเกลี่ยอัตรากำลัง
          </v-card-title>

          <v-card-text class="pt-4">
            <v-alert type="warning" outlined dense class="mb-3">
              รูปแบบคอลัมน์: เลขที่ตำแหน่ง, รหัสสถานศึกษาเดิม, ชื่อวิทยาลัยเดิม, รหัสสถานศึกษาใหม่,
              ชื่อวิทยาลัยใหม่, หมายเหตุ — ระบบจะตรวจสอบเลขที่ตำแหน่งและวิทยาลัยต้นทางกับข้อมูลจริงใน
              จอร.18 ก่อนนำเข้าเสมอ แถวที่ไม่ตรงจะไม่ถูกนำเข้าและแจ้งเหตุผลให้ทราบ
            </v-alert>

            <v-file-input
              v-model="reallocationFile"
              accept=".csv"
              label="ไฟล์ CSV แผนเกลี่ยอัตรากำลัง"
              prepend-icon="mdi-paperclip"
              outlined
              show-size
              counter
            />

            <v-btn
              block
              large
              color="info"
              @click="reallocationCSVSubmit"
              :disabled="!reallocationFile"
              :loading="reallocationImporting"
            >
              <v-icon left>mdi-upload</v-icon>
              นำเข้าข้อมูล
            </v-btn>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn icon large color="grey" @click="reallocationCSVdialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Add Dialog -->
      <v-dialog v-model="addjor18dialog" persistent max-width="600px">
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-account-multiple</v-icon>
            เพิ่มข้อมูลประเภทสถานศึกษา
          </v-card-title>

          <v-card-text class="pt-4">
            <v-form ref="addjor18form" v-model="valid" lazy-validation>
              <v-autocomplete
                v-model="addjor18.j18college_code"
                :items="colleges"
                item-text="college_name"
                item-value="college_code"
                label="วิทยาลัย"
                :rules="[v => !!v || 'กรุณาเลือกวิทยาลัย']"
                outlined
              />

              <v-text-field
                v-model="addjor18.id_position"
                label="เลขที่ตำแหน่ง"
                :rules="[v => !!v || 'กรุณาระบุเลขที่ตำแหน่ง']"
                outlined
              />

              <v-autocomplete
                v-model="addjor18.positions"
                :items="userstatus"
                item-text="user_status_name"
                item-value="user_status_name"
                label="ตำแหน่ง"
                :rules="[v => !!v || 'กรุณาเลือกตำแหน่ง']"
                outlined
              />
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn color="grey" text @click="addjor18dialog = false">
              <v-icon left>mdi-close</v-icon>
              ยกเลิก
            </v-btn>
            <v-btn color="success" @click="addjor18Submit">
              <v-icon left>mdi-content-save</v-icon>
              บันทึก
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Edit Dialog -->
      <v-dialog v-model="editjor18dialog" persistent max-width="600px">
        <v-card>
          <v-card-title class="warning white--text">
            <v-icon left color="white">mdi-pencil</v-icon>
            แก้ไขข้อมูล
          </v-card-title>

          <v-card-text class="pt-4">
            <v-form ref="editjor18form" v-model="valid" lazy-validation>
              <div class="mb-4">
                เลขที่ตำแหน่ง: <strong>{{ editjor18.id_position }}</strong> | 
                ตำแหน่ง: <strong>{{ editjor18.positions }}</strong>
              </div>

              <v-autocomplete
                v-model="editjor18.j18college_code"
                :items="colleges"
                item-text="college_name"
                item-value="college_code"
                label="วิทยาลัย"
                :rules="[v => !!v || 'กรุณาเลือกวิทยาลัย']"
                outlined
              />

              <v-autocomplete
                v-model="editjor18.j18_reason"
                :items="reasons"
                item-text="text"
                item-value="value"
                label="เหตุผล"
                :rules="[v => !!v || 'กรุณาเลือกเหตุผล']"
                outlined
              />

              <v-textarea
                v-if="editjor18.j18_reason === 'etc'"
                v-model="editjor18.j18_reason_etc"
                label="เหตุผลอื่นๆ"
                :rules="[v => !!v || 'กรุณาระบุเหตุผลอื่นๆ']"
                outlined
              />
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn color="grey" text @click="editjor18dialog = false">
              <v-icon left>mdi-close</v-icon>
              ยกเลิก
            </v-btn>
            <v-btn color="warning" @click="editjor18Submit">
              <v-icon left>mdi-pencil</v-icon>
              บันทึก
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Dialog -->
      <v-dialog v-model="deletejor18dialog" persistent max-width="500px">
        <v-card>
          <v-card-title class="error white--text">
            <v-icon left color="white">mdi-delete</v-icon>
            ลบข้อมูลประเภทสถานศึกษา
          </v-card-title>

          <v-card-text class="pt-4">
            <v-alert type="warning" outlined>
              ยืนยันการลบข้อมูล<br>
              <strong>{{ editjor18.college_name }} : {{ editjor18.id_position }}</strong>
            </v-alert>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn color="grey" text @click="deletejor18dialog = false">
              <v-icon left>mdi-close</v-icon>
              ยกเลิก
            </v-btn>
            <v-btn color="error" @click="deletejor18Submit">
              <v-icon left>mdi-delete</v-icon>
              ลบ
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- CSV Import Dialog -->
      <v-dialog v-model="man_powerCSVdialog" persistent max-width="600px">
        <v-card>
          <v-card-title class="success white--text">
            <v-icon left color="white">mdi-file-import</v-icon>
            นำเข้าข้อมูลอัตราสำรวจ
          </v-card-title>

          <v-card-text class="pt-4">
            <v-btn
              block
              large
              color="info"
              :to="`/admin/jo18_survey_import/`"
              target="_blank"
              class="mb-4"
            >
              <v-icon left>mdi-download</v-icon>
              ส่งออกแบบฟอร์มบันทึกข้อมูล
            </v-btn>

            <v-select
              v-model="csvImportPosition"
              :items="['ครู', 'ผู้อำนวยการวิทยาลัย', 'รองผู้อำนวยการวิทยาลัย']"
              label="ตำแหน่งของข้อมูลที่นำเข้า"
              outlined
              dense
              class="mb-2"
            />

            <v-file-input
              v-model="file2"
              accept=".csv"
              label="ไฟล์ CSV อัตรากำลัง"
              prepend-icon="mdi-paperclip"
              outlined
              show-size
              counter
            />

            <v-btn
              block
              large
              color="success"
              @click="csvManpowerSubmit"
              :disabled="!file2"
            >
              <v-icon left>mdi-upload</v-icon>
              นำเข้าข้อมูล
            </v-btn>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn icon large color="grey" @click="man_powerCSVdialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        top
      >
        <v-icon left>{{ snackbar.icon }}</v-icon>
        {{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">
            ปิด
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import jor_18bar from "../../components/admin/jor_18bar";
import PositionDensityMap from "../../components/core/PositionDensityMap.vue";
import * as XLSX from "xlsx";
import { logSensitiveAccess } from "@/utils/sensitiveAccessLog";

export default {
  components: { jor_18bar, PositionDensityMap },

  data() {
    return {
      ApiKey: "HRvec2021",
      valid: true,
      loading: false,
      file2: null,

      // Dialogs
      addjor18dialog: false,
      editjor18dialog: false,
      deletejor18dialog: false,
      man_powerCSVdialog: false,
      csvImportPosition: "ครู",

      // Monitoring dashboard
      stats: {},
      positionTypeHeaders: [
        { text: "ประเภทตำแหน่ง", value: "positions" },
        { text: "ทั้งหมด", value: "total", align: "end" },
        { text: "ครองแล้ว", value: "occupied", align: "end" },
        { text: "ว่าง", value: "vacant", align: "end" },
        { text: "สัดส่วนครอง/ว่าง", value: "ratio", sortable: false }
      ],
      collegeSummary: null,
      collegeSummaryLoading: false,
      provinceStats: [],
      criterionRows: [],
      anomalies: {},
      anomaliesLoading: false,
      anomalyDialog: false,
      anomalyDialogTab: "duplicate_incumbent",
      anomalyCategories: [
        { key: "duplicate_incumbent", label: "ผู้ครองซ้อนตำแหน่ง", type: "error", color: "red darken-2" },
        { key: "stale_incumbent", label: "ข้อมูลผู้ครองไม่ตรงกับปัจจุบัน", type: "warning", color: "amber darken-3" },
        { key: "incumbent_not_found", label: "ไม่พบข้อมูลผู้ครอง", type: "warning", color: "amber darken-3" },
        { key: "overdue_retiree", label: "เลยกำหนดเกษียณแต่ยังเป็นผู้ครอง", type: "error", color: "red darken-2" }
      ],

      // แผนเกลี่ยอัตรากำลัง
      reallocations: [],
      reallocationLoading: false,
      reallocationStatusFilter: "pending",
      reallocationCSVdialog: false,
      reallocationFile: null,
      reallocationImporting: false,
      reallocationHeaders: [
        { text: "เลขที่ตำแหน่ง", value: "position" },
        { text: "เส้นทาง", value: "route" },
        { text: "ผู้ครองตำแหน่งปัจจุบัน", value: "current_owner" },
        { text: "หมายเหตุ", value: "note" },
        { text: "นำเข้าโดย", value: "imported" },
        { text: "จัดการ", value: "actions" }
      ],

      // Snackbar
      snackbar: {
        show: false,
        color: "",
        timeout: 5000,
        icon: "",
        text: ""
      },

      // Data
      jor18s: [],
      colleges: [],
      userstatus: [],
      addjor18: {},
      editjor18: {},

      // Search
      search: "",
      statusFilter: "all",
      collegesSelect: "",
      idPositionSelect: "",

      // Table
      headers: [],
      selectedHeaders: [],
      headersMap: [
        { text: "รหัสวิทยาลัย", value: "j18college_code" },
        { text: "สถานศึกษา", value: "college_name" },
        { text: "จังหวัด", value: "province_name" },
        { text: "ภาค", value: "region_name" },
        { text: "อัตราว่าง", value: "num_position" },
        { text: "ตำแหน่ง", value: "positions" },
        { text: "รหัสตำแหน่ง", value: "id_position" },
        { text: "จัดการ", value: "actionSelect" },
        { text: "นำมาใช้", value: "j18_reason" },
        { text: "สำรวจ", value: "j18_collegereport" },
        { text: "เหตุผลอื่น", value: "j18_reason_etc" },
        { text: "เหตุการณ์", value: "j18_background" },
        { text: "วิทยฐานะ", value: "rang_positon" },
        { text: "ID คนครอง", value: "id_card" },
        { text: "คนครอง", value: "titles" },
        { text: "แก้ไข", value: "actions" },
        { text: "ลบ", value: "action_s" }
      ],

      // Reasons
      reasons: [
        { text: "คัดเลือก", value: "คัดเลือก" },
        { text: "สอบแข่งขัน", value: "สอบแข่งขัน" },
        { text: "ย้าย", value: "ย้าย" },
        { text: "อื่นๆ", value: "etc" }
      ],

      // Edit
      editedIndex: -1,
      editedItem: {
        id_no: 0,
        j18_reason: ""
      },
      defaultItem: {
        id_no: 0,
        j18_reason: ""
      },

      data_syslog: {}
    };
  },

  computed: {
    showHeaders() {
      return this.headers.filter(h => this.selectedHeaders.includes(h));
    },

    date_today_log() {
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, "0");
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const yyyy = today.getFullYear() + 543;
      const time = `${today.getHours()}:${today.getMinutes()}:${today.getSeconds()}`;
      return `${dd}/${mm}/${yyyy}/${time}`;
    },

    anomalyTotal() {
      if (!this.anomalies.counts) return 0;
      return Object.values(this.anomalies.counts).reduce((s, n) => s + (n || 0), 0);
    },

    // กรองตามสถานะ (ทั้งหมด/มีคนครอง/ว่าง) ก่อนเข้าตาราง — ใช้ร่วมกับช่องค้นหาข้อความของ v-data-table ได้ในตัว
    displayedJor18s() {
      if (this.statusFilter === "occupied") return this.jor18s.filter(this.hasIdCard);
      if (this.statusFilter === "vacant") return this.jor18s.filter(item => !this.hasIdCard(item));
      return this.jor18s;
    }
  },

  async created() {
    this.headers = Object.values(this.headersMap);
    this.selectedHeaders = this.headers;
  },

  async mounted() {
    await this.initialize();
    logSensitiveAccess(this.$http, "jor18", "view", "ภาพรวมกรอบอัตรากำลัง/ตำแหน่งว่าง");
  },

  methods: {
    async initialize() {
      await this.showLoading();

      try {
        const [collegesRes, userStatusRes] = await Promise.all([
          this.$http.post("college.php", { ApiKey: this.ApiKey }),
          this.$http.post("crud_user_status.php?crud=read", { ApiKey: this.ApiKey })
        ]);

        this.colleges = collegesRes.data;
        this.userstatus = userStatusRes.data;

        await this.jor18QueryAll();
        await this.loadStats();
        await this.reallocationQuery();
        this.loadProvinceStats();
        this.loadAnomalies();
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการโหลดข้อมูล");
      } finally {
        this.hideLoading();
      }
    },

    statOrDash(val) {
      return val === undefined || val === null ? "-" : val;
    },

    formatNum(n) {
      if (n === null || n === undefined) return "0";
      return (+n).toLocaleString("th-TH");
    },

    occupiedPct(row) {
      const total = +row.total || 0;
      if (total === 0) return 0;
      return Math.round(((+row.occupied || 0) / total) * 100);
    },

    async loadStats() {
      try {
        const result = await this.$http.post("jor18.stats.php", { ApiKey: this.ApiKey });
        if (result.data && result.data.status) {
          this.stats = result.data;
          if (this.stats.latest_rate_work_g_year) {
            this.loadCriterionRows(this.stats.latest_rate_work_g_year);
          }
        }
      } catch (error) {
        console.error("โหลดสถิติภาพรวมไม่สำเร็จ:", error);
      }
    },

    async loadProvinceStats() {
      try {
        const result = await this.$http.post("jor18.province_stats.php", { ApiKey: this.ApiKey });
        if (result.data && result.data.status) {
          this.provinceStats = result.data.rows || [];
        }
      } catch (error) {
        console.error("โหลดสถิติรายจังหวัดไม่สำเร็จ:", error);
      }
    },

    async loadCriterionRows(year) {
      try {
        const result = await this.$http.post("rate_work_g.php", {
          ApiKey: this.ApiKey,
          rate_work_g_year: String(year)
        });
        this.criterionRows = Array.isArray(result.data) ? result.data : [];
      } catch (error) {
        console.error("โหลดข้อมูลเกณฑ์อัตรากำลังไม่สำเร็จ:", error);
      }
    },

    async loadAnomalies() {
      this.anomaliesLoading = true;
      try {
        const result = await this.$http.post("jor18.anomalies.php", { ApiKey: this.ApiKey });
        if (result.data && result.data.status) {
          this.anomalies = result.data;
        }
      } catch (error) {
        console.error("ตรวจสอบความผิดปกติไม่สำเร็จ:", error);
      } finally {
        this.anomaliesLoading = false;
      }
    },

    async reallocationQuery() {
      this.reallocationLoading = true;
      try {
        const result = await this.$http.post("position_reallocation.php", {
          ApiKey: this.ApiKey,
          status: this.reallocationStatusFilter
        });
        this.reallocations = Array.isArray(result.data) ? result.data : [];
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการโหลดแผนเกลี่ยอัตรากำลัง");
      } finally {
        this.reallocationLoading = false;
      }
    },

    async reallocationCSVSubmit() {
      if (!this.reallocationFile) return;

      const formData = new FormData();
      formData.append("file", this.reallocationFile);
      formData.append("ApiKey", this.ApiKey);

      this.reallocationImporting = true;
      try {
        const result = await this.$http.post(
          "position_reallocation.import.php",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        if (result.data && result.data.status) {
          const d = result.data;
          const detailLines = (d.skipped_detail || []).join("<br>");
          await Swal.fire({
            icon: "success",
            title: "นำเข้าข้อมูลเรียบร้อย",
            html:
              `<p>นำเข้าสำเร็จ ${d.inserted} รายการ</p>` +
              (d.not_found_in_jor18 || d.duplicate_pending || d.college_from_mismatch
                ? `<p class="text-left" style="font-size:13px;color:#888;">` +
                  `ไม่พบใน จอร.18: ${d.not_found_in_jor18} | ซ้ำกับแผนเดิม: ${d.duplicate_pending} | ` +
                  `วิทยาลัยต้นทางไม่ตรง: ${d.college_from_mismatch}<br>${detailLines}</p>`
                : "")
          });
          this.reallocationCSVdialog = false;
          this.reallocationFile = null;
          this.reallocationStatusFilter = "pending";
          await this.reallocationQuery();
          await this.loadStats();
        } else {
          this.showWarning((result.data && result.data.message) || "นำเข้าข้อมูลผิดพลาด");
        }
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการนำเข้าข้อมูล");
      } finally {
        this.reallocationImporting = false;
      }
    },

    async confirmReallocation(item, force) {
      const confirmed = await this.confirmDialog(
        force
          ? "ตำแหน่งนี้ยังมีผู้ครองอยู่ในระบบ ยืนยันว่าพ้นตำแหน่งแล้วจริงหรือไม่?"
          : `ยืนยันว่าอัตรา ${item.id_position} ย้ายไป ${item.college_name_to_live || item.college_code_to} แล้วจริง?`
      );
      if (!confirmed) return;

      try {
        const result = await this.$http.post(
          "position_reallocation.confirm.php",
          { ApiKey: this.ApiKey, id: item.id, force: !!force }
        );

        if (result.data && result.data.status) {
          this.showSuccess("ยืนยันย้ายอัตราเรียบร้อย");
          await this.reallocationQuery();
          await this.loadStats();
        } else if (result.data && result.data.still_occupied && !force) {
          await this.confirmReallocation(item, true);
        } else {
          this.showWarning((result.data && result.data.message) || "ดำเนินการผิดพลาด");
        }
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการยืนยัน");
      }
    },

    async cancelReallocation(item) {
      const { value: reason, isConfirmed } = await Swal.fire({
        title: `ยกเลิกแผนเกลี่ยอัตรา ${item.id_position}?`,
        input: "text",
        inputLabel: "เหตุผล (ถ้ามี)",
        showCancelButton: true,
        confirmButtonText: "ยกเลิกแผนนี้",
        cancelButtonText: "ปิด"
      });
      if (!isConfirmed) return;

      try {
        const result = await this.$http.post(
          "position_reallocation.cancel.php",
          { ApiKey: this.ApiKey, id: item.id, cancel_reason: reason || "" }
        );

        if (result.data && result.data.status) {
          this.showSuccess("ยกเลิกแผนเกลี่ยเรียบร้อย");
          await this.reallocationQuery();
          await this.loadStats();
        } else {
          this.showWarning((result.data && result.data.message) || "ดำเนินการผิดพลาด");
        }
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการยกเลิก");
      }
    },

    retireStatusLabel(status) {
      const map = {
        retiring_this_year: "เกษียณปีนี้",
        overdue: "เลยกำหนดเกษียณ",
        unknown: "ไม่ทราบวันเกิด",
        active: "ยังไม่ถึงกำหนด"
      };
      return map[status] || "-";
    },

    retireStatusColor(status) {
      const map = {
        retiring_this_year: "success",
        overdue: "error",
        unknown: "grey",
        active: "info"
      };
      return map[status] || "grey";
    },

    hasIdCard(item) {
      return String(item.id_card).length > 5;
    },

    async csvManpowerSubmit() {
      if (!this.file2) return;

      const formData = new FormData();
      formData.append("file", this.file2);
      formData.append("filename", "../HRvecfiles/jor18Survey.csv");
      formData.append("ApiKey", this.ApiKey);
      formData.append("positions", this.csvImportPosition);

      try {
        const result = await this.$http.post("jor18_csv_survey.php", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });

        if (result.data.status) {
          this.showSuccess("บันทึกข้อมูลเรียบร้อย");
          await this.searchByPosition(this.csvImportPosition);
        } else {
          this.showWarning("ดำเนินการผิดพลาด");
        }
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการนำเข้าข้อมูล");
      } finally {
        this.man_powerCSVdialog = false;
        this.file2 = null;
      }
    },

    man_powerCSV() {
      this.man_powerCSVdialog = true;
    },

    // ป้ายกำกับ "กรณี" เมื่อเลขตำแหน่งว่างลงจากประวัติการพ้นสภาพ (personnel_temporary_del.ptdel_status_del)
    ptdelStatusLabel(code) {
      return {
        resign: "เลขว่างลาออก",
        discard: "เลขว่างให้ออก",
        dismiss: "เลขว่างไล่ออก",
        transfer: "เลขว่างโอนย้าย",
        retire: "เลขว่างเกษียณ",
        died: "เลขว่างเสียชีวิต"
      }[code] || "";
    },

    downloadXlsx(rows, sheetName, filename) {
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      XLSX.writeFile(wb, filename);
    },

    // ส่งออกตำแหน่งว่างทั้งหมด (เดิมเปิดแท็บใหม่ไปหน้า print_jor18_null แล้วต้องกดปุ่ม Export to excel อีกชั้น)
    async exportVacantAll() {
      this.loading = true;
      try {
        const result = await this.$http.post("jor18.php", {
          ApiKey: this.ApiKey,
          idpostion_null: "OK"
        });
        const rows = (Array.isArray(result.data) ? result.data : []).map((item, index) => ({
          ที่: index + 1,
          รหัสวิทยาลัย: item.college_code,
          สถานศึกษา: item.college_name,
          จังหวัด: item.province_name,
          ภาค: item.region_name,
          อัตราว่างจำนวน: item.num_position,
          เลขที่ตำแหน่ง: item.id_position,
          ตำแหน่ง: item.positions,
          กรณี: this.ptdelStatusLabel(item.ptdel_status_del),
          เหตุการณ์: item.j18_background || "-",
          การนำมาใช้: item.j18_reason || "-",
          เหตุผลอื่น: item.j18_reason_etc || "-"
        }));
        logSensitiveAccess(this.$http, "jor18", "export", "ตำแหน่งว่างทั้งหมด_jor18.xlsx");
        this.downloadXlsx(rows, "ตำแหน่งว่าง", "ตำแหน่งว่างทั้งหมด_jor18.xlsx");
        this.showSuccess(`ส่งออกไฟล์ Excel เรียบร้อย (${rows.length} รายการ)`);
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการส่งออก");
      } finally {
        this.loading = false;
      }
    },

    // ส่งออกตำแหน่งว่าง ครู (สำรวจ) — เดิมเปิดแท็บใหม่ไปหน้า print_jor18_teacherNull
    async exportTeacherSurvey() {
      this.loading = true;
      try {
        const result = await this.$http.post("jor18_select_survey.php", {
          ApiKey: this.ApiKey,
          j18_collegereport: "1"
        });
        const rows = (Array.isArray(result.data) ? result.data : []).map((item, index) => ({
          ที่: index + 1,
          รหัสวิทยาลัย: item.college_code,
          สถานศึกษา: item.college_name,
          จังหวัด: item.province_name,
          ภาค: item.region_name,
          อัตราว่างจำนวน: item.num_position,
          เลขที่ตำแหน่ง: item.id_position,
          ตำแหน่ง: item.positions,
          กรณี: this.ptdelStatusLabel(item.ptdel_status_del),
          เหตุการณ์: item.j18_background || "-",
          การนำมาใช้: item.j18_reason || "-",
          เหตุผลอื่น: item.j18_reason_etc || "-"
        }));
        logSensitiveAccess(this.$http, "jor18", "export", "ตำแหน่งว่างครูสำรวจ_jor18.xlsx");
        this.downloadXlsx(rows, "ครูสำรวจ", "ตำแหน่งว่างครูสำรวจ_jor18.xlsx");
        this.showSuccess(`ส่งออกไฟล์ Excel เรียบร้อย (${rows.length} รายการ)`);
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการส่งออก");
      } finally {
        this.loading = false;
      }
    },

    // ส่งออกตำแหน่งว่าง ครู (สำรวจย้าย) — เดิมเปิดแท็บใหม่ไปหน้า print_jor18_teacherMove
    async exportTeacherMove() {
      this.loading = true;
      try {
        const periodResult = await this.$http.post("period_college.php", {
          ApiKey: this.ApiKey,
          period_college_id: "401"
        });
        const period = periodResult.data || {};
        const result = await this.$http.post("jor18.php", {
          ApiKey: this.ApiKey,
          college_manpower_year: period.period_college_year,
          college_manpower_time: period.period_college_times,
          college_manpower_move: "ok"
        });
        const rows = (Array.isArray(result.data) ? result.data : []).map((item, index) => ({
          ที่: index + 1,
          รหัสวิทยาลัย: item.j18college_code,
          สถานศึกษา: item.college_name,
          จังหวัด: item.province_name,
          ภาค: item.region_name,
          เลขที่ตำแหน่ง: item.id_position,
          การนำมาใช้: item.j18_reason || "-",
          สาขาที่ประสงค์รับย้าย: item.name_branchs || "-",
          หมายเหตุ: item.conditions_branch_comment || "-"
        }));
        logSensitiveAccess(this.$http, "jor18", "export", "ตำแหน่งว่างครูสำรวจย้าย_jor18.xlsx");
        this.downloadXlsx(rows, "ครูสำรวจย้าย", "ตำแหน่งว่างครูสำรวจย้าย_jor18.xlsx");
        this.showSuccess(`ส่งออกไฟล์ Excel เรียบร้อย (${rows.length} รายการ)`);
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการส่งออก");
      } finally {
        this.loading = false;
      }
    },

    // ตรงกับตัวกรองข้อความของ v-data-table (ค้นหาทุกคอลัมน์แบบไม่สนตัวพิมพ์เล็ก/ใหญ่) —
    // ให้ไฟล์ที่ส่งออกตรงกับที่กำลังเห็นอยู่บนตารางเสมอ ไม่ใช่ข้อมูลทั้งหมดที่โหลดไว้
    matchesSearch(item) {
      if (!this.search) return true;
      const q = this.search.toString().toLowerCase();
      return Object.values(item).some(
        v => v !== null && v !== undefined && String(v).toLowerCase().includes(q)
      );
    },

    exportToExcel() {
      try {
        logSensitiveAccess(
          this.$http,
          "jor18",
          "export",
          "อัตรากำลังขาดเกินเกณฑ์_jor18.xlsx"
        );

        const rows = this.displayedJor18s.filter(this.matchesSearch);
        const data = rows.map(item => {
          const row = {};
          this.showHeaders.forEach(h => {
            if (h.value.startsWith("action")) return;
            const val = item[h.value];
            row[h.text] = val !== null && val !== undefined && val !== "" ? val : "-";
          });
          return row;
        });

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Jor18");
        XLSX.writeFile(wb, `อัตรากำลังขาดเกินเกณฑ์_jor18.xlsx`);

        this.showSuccess(`ส่งออกไฟล์ Excel เรียบร้อย (${data.length} รายการ)`);
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการส่งออก");
      }
    },

    editItem(item) {
      this.editedIndex = this.jor18s.indexOf(item);
      this.editedItem = Object.assign({}, item);
    },

    close() {
      this.editedItem = Object.assign({}, this.defaultItem);
      this.editedIndex = -1;
    },

    async save() {
      try {
        this.editedItem.ApiKey = this.ApiKey;
        Object.assign(this.jor18s[this.editedIndex], this.editedItem);

        const result = await this.$http.post("jor18.updateIdno.php", this.editedItem);

        if (result.data.status) {
          this.showSuccess("บันทึกข้อมูลเรียบร้อย");
        } else {
          this.showWarning("ทำรายการซ้ำ");
        }
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการบันทึก");
      } finally {
        this.close();
      }
    },

    async clearSurvey() {
      const confirmed = await this.confirmDialog(
        "ต้องการยกเลิกรายการสำรวจทั้งหมด?"
      );

      if (!confirmed) return;

      try {
        this.editedItem.ApiKey = this.ApiKey;
        const result = await this.$http.post(
          "jor18.updateclearSurvey.php",
          this.editedItem
        );

        if (result.data.status) {
          this.showSuccess("ดำเนินการเรียบร้อย");
          await this.searchTeacher();
        } else {
          this.showWarning("ผิดพลาด");
        }
      } catch (error) {
        this.showError("เกิดข้อผิดพลาด");
      }
    },

    async deleteItem(id_no) {
      const confirmed = await this.confirmDialog(
        "ต้องการยกเลิกรายการสำรวจนี้?"
      );

      if (!confirmed) return;

      try {
        const payload = {
          ApiKey: this.ApiKey,
          id_no: id_no,
          j18_reason: "",
          j18_collegereport: 0
        };

        const result = await this.$http.post("jor18.updateIdno.php", payload);

        if (result.data.status) {
          this.showSuccess("ดำเนินการเรียบร้อย");
          await this.searchTeacher();
        } else {
          this.showWarning("ผิดพลาด");
        }
      } catch (error) {
        this.showError("เกิดข้อผิดพลาด");
      }
    },

    async searchByPosition(position) {
      await this.showLoading();
      this.loading = true;

      try {
        const result = await this.$http.post("jor18.php", {
          ApiKey: this.ApiKey,
          positions: position,
          idpostion_null: "ok"
        });

        this.jor18s = result.data;
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการค้นหา");
      } finally {
        this.loading = false;
        this.hideLoading();
      }
    },

    async searchTeacher() {
      await this.searchByPosition("ครู");
    },

    async searchSecDirector() {
      await this.searchByPosition("รองผู้อำนวยการวิทยาลัย");
    },

    async searchDirector() {
      await this.searchByPosition("ผู้อำนวยการวิทยาลัย");
    },

    async updatej18background() {
      try {
        this.updatemanpowers.ApiKey = this.ApiKey;
        const result = await this.$http.post(
          "man_power.insert.j18.php",
          this.updatemanpowers
        );

        if (result.data.status) {
          this.showSuccess("ปรับปรุงข้อมูลอัตรากำลังรับย้ายเรียบร้อย");
        }
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการปรับปรุง");
      }
    },

    async SearchNull() {
      await this.showLoading();
      this.loading = true;

      try {
        const result = await this.$http.post("jor18.php", {
          ApiKey: this.ApiKey,
          idpostion_null: "ok"
        });

        this.jor18s = result.data;
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการค้นหา");
      } finally {
        this.loading = false;
        this.hideLoading();
      }
    },

    clearSearch() {
      this.jor18s = [];
      this.collegesSelect = "";
      this.idPositionSelect = "";
      this.search = "";
      this.collegeSummary = null;
    },

    async loadCollegeSummary(college_code) {
      if (!college_code) {
        this.collegeSummary = null;
        return;
      }
      this.collegeSummaryLoading = true;
      try {
        const result = await this.$http.post("jor18.college_summary.php", {
          ApiKey: this.ApiKey,
          college_code
        });
        this.collegeSummary = (result.data && result.data.status) ? result.data : null;
      } catch (error) {
        console.error("โหลดสรุปวิทยาลัยไม่สำเร็จ:", error);
        this.collegeSummary = null;
      } finally {
        this.collegeSummaryLoading = false;
      }
    },

    async jor18sSearch() {
      if (!this.collegesSelect) return;

      this.loading = true;
      this.loadCollegeSummary(this.collegesSelect);
      try {
        const result = await this.$http.post("jor18.php", {
          ApiKey: this.ApiKey,
          j18college_code: this.collegesSelect
        });

        this.jor18s = result.data;
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการค้นหา");
      } finally {
        this.loading = false;
      }
    },

    async jor18sIDpostionSearch() {
      if (!this.idPositionSelect) return;

      this.loading = true;
      try {
        const result = await this.$http.post("jor18.php", {
          ApiKey: this.ApiKey,
          id_position: this.idPositionSelect
        });

        this.jor18s = result.data;
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการค้นหา");
      } finally {
        this.loading = false;
      }
    },

    async jor18QueryAll() {
      this.loading = true;
      try {
        const result = await this.$http.post("jor18.php", {
          ApiKey: this.ApiKey
        });

        this.jor18s = result.data;
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการโหลดข้อมูล");
      } finally {
        this.loading = false;
      }
    },

    jor18Add() {
      this.addjor18 = {};
      this.addjor18dialog = true;
    },

    async addjor18Submit() {
      if (!this.$refs.addjor18form.validate()) return;

      try {
        this.addjor18.ApiKey = this.ApiKey;
        this.addjor18.id_card = "";

        const result = await this.$http.post("jor18.insert.php", this.addjor18);

        if (result.data.status) {
          this.showSuccess("บันทึกข้อมูลเรียบร้อย");
          await this.logAction("insert", `${this.addjor18.id_position}:${this.addjor18.college_code}`);
          await this.jor18QueryAll();
        } else {
          this.showWarning("บันทึกข้อมูลผิดพลาด");
        }
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการบันทึก");
      } finally {
        this.addjor18dialog = false;
      }
    },

    async jor18Edit(id_position, positions) {
      try {
        const result = await this.$http.post("jor18.php", {
          ApiKey: this.ApiKey,
          id_position: id_position,
          positions: positions
        });

        this.editjor18 = result.data;
        this.editjor18dialog = true;
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการโหลดข้อมูล");
      }
    },

    async editjor18Submit() {
      if (!this.$refs.editjor18form.validate()) return;

      try {
        this.editjor18.ApiKey = this.ApiKey;
        const result = await this.$http.post(
          "jor18.updateIdPosition.php",
          this.editjor18
        );

        if (result.data.status) {
          this.showSuccess("แก้ไขข้อมูลเรียบร้อย");
          await this.logAction(
            "update",
            `${this.editjor18.id_position}:${this.editjor18.college_code}:${this.editjor18.case_vacancy}`
          );
          await this.jor18QueryAll();
        } else {
          this.showWarning("แก้ไขข้อมูลผิดพลาด");
        }
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการแก้ไข");
      } finally {
        this.editjor18dialog = false;
      }
    },

    async jor18Delete(id_position, positions) {
      try {
        const result = await this.$http.post("jor18.php", {
          ApiKey: this.ApiKey,
          id_position: id_position,
          positions: positions
        });

        this.editjor18 = result.data;
        this.deletejor18dialog = true;
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการโหลดข้อมูล");
      }
    },

    async deletejor18Submit() {
      if (!this.$refs.deletejor18form) return;

      try {
        this.editjor18.ApiKey = this.ApiKey;
        const result = await this.$http.post("jor18.delete.php", this.editjor18);

        if (result.data.status) {
          this.showSuccess("ลบข้อมูลเรียบร้อย");
          await this.logAction(
            "delete",
            `${this.editjor18.id_position}:${this.editjor18.college_code}:${this.editjor18.case_vacancy}`
          );
          await this.jor18QueryAll();
        } else {
          this.showWarning("ลบข้อมูลผิดพลาด");
        }
      } catch (error) {
        this.showError("เกิดข้อผิดพลาดในการลบ");
      } finally {
        this.deletejor18dialog = false;
      }
    },

    async logAction(event, detail) {
      try {
        const userSession = JSON.parse(localStorage.getItem("user")) || {};
        
        const logData = {
          ApiKey: this.ApiKey,
          user_account: userSession.user_name,
          event_log: event,
          page_log: "jor18",
          table_log: "jor18",
          detail_log: detail,
          date_times: this.date_today_log
        };

        await this.$http.post("data_syslog.insert.php", logData);
      } catch (error) {
        console.error("Failed to log action:", error);
      }
    },

    async showLoading() {
      Swal.fire({
        title: "กรุณารอสักครู่...",
        html: "กำลังโหลดข้อมูล",
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
    },

    hideLoading() {
      Swal.close();
    },

    showSuccess(message) {
      Swal.fire({
        icon: "success",
        title: message,
        showConfirmButton: false,
        timer: 1500
      });
    },

    showWarning(message) {
      Swal.fire({
        icon: "warning",
        title: message,
        showConfirmButton: false,
        timer: 1500
      });
    },

    showError(message) {
      Swal.fire({
        icon: "error",
        title: message,
        showConfirmButton: false,
        timer: 2000
      });
    },

    async confirmDialog(message) {
      const result = await Swal.fire({
        title: message,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#b3151b",
        cancelButtonColor: "#d33",
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก"
      });

      return result.isConfirmed;
    }
  }
};
</script>

<style scoped>
.v-data-table {
  font-size: 14px;
}

.v-chip {
  font-size: 12px;
}

.dash-card {
  border-radius: 10px;
}
</style>